/* eslint-disable camelcase */
/* eslint-disable react/display-name */
/**
 * @file CountryUserRolesManagementView.jsx
 * @author Fernando Ruano
 * @description CountryUserRoles Management View Page
 */

// Dependencies
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

// Helpers
import { sortString } from '@nephro/commons/helpers/utils';

// Components
import ManagementCrud from '@nephro/components/ManagementCrud';

/**
 * CountryUserRoles Management View
 * @param { Object } countryUserRoleActions - CountryUserRole Actions.
 * @return { Node } React node.
 */
function CountryUserRolesManagementView({
  countryUserRoleActions,
  userActions,
  dashboardStaffActions
}) {
  const [rowData, setRowData] = useState([]);
  const [clinicsList, setClinicsList] = useState(null);
  const { t: translate } = useTranslation();
  const { getCountryUserRolesArray } = countryUserRoleActions;
  const { deleteUserById } = userActions;
  const { getAdmonCodesByEntity } = dashboardStaffActions;

  const findClinic = (idClinic) => {
    if (!idClinic) return null;
    const clinic = clinicsList?.find(({ codigo_id }) => codigo_id === idClinic);
    return clinic?.nombre;
  };

  /**
   * Maps items to create row data structure.
   * @param { Object } id - Item id.
   * @param { Object } user_response - User data.
   * @param { Object } country_response - Country data.
   * @param { Object } profile_response - Profile data.
   * @param { Object } role_response - Role data.
   * @param { number } id_clinic - id Clinic.
   * @return { Object } - Transformed item.
   */
  const mapToRowData = ({ id, user, country, profile, role, id_clinic }) => {
    const rowItemObject = {
      id,
      userName: user.username,
      email: user.email,
      names: user.first_name,
      lastNames: user.last_name,
      roleName: role.name,
      profile: profile.name,
      country,
      defaultClinic: findClinic(id_clinic),
      user
    };
    return rowItemObject;
  };

  // Table columns
  const columns = [
    {
      title: translate('curMg.userName'),
      dataIndex: 'userName',
      sorter: sortString('userName'),
      ellipsis: true
    },
    {
      title: translate('curMg.email'),
      dataIndex: 'email',
      sorter: sortString('email'),
      ellipsis: true
    },
    {
      title: translate('curMg.names'),
      dataIndex: 'names',
      sorter: sortString('first_name'),
      ellipsis: true
    },
    {
      title: translate('curMg.lastNames'),
      dataIndex: 'lastNames',
      sorter: sortString('last_name'),
      ellipsis: true
    },
    {
      title: translate('curMg.roleName'),
      dataIndex: 'roleName',
      sorter: sortString('roleName'),
      ellipsis: true
    },
    {
      title: translate('curMg.profile'),
      dataIndex: 'profile',
      sorter: sortString('profile'),
      ellipsis: true
    },
    {
      title: translate('curMg.defaultClinic'),
      dataIndex: 'defaultClinic',
      sorter: sortString('defaultClinic'),
      ellipsis: true
    }
  ];

  const onDeleteItem = ({ id: deleteId }, callback) => {
    const { user } = rowData.find(({ id }) => id === deleteId);
    deleteUserById({ id: user.user_data_id }, callback);
  };

  // Function to get and set clinic list
  const onGetClinicsList = (_, response) => {
    if (response?.data) {
      setClinicsList(response.data);
    }
  };

  const onGetRowData = (_, response) => {
    if (response?.data?.content) {
      const entityId = 2;
      if (!clinicsList) {
        getAdmonCodesByEntity({ entityId }, onGetClinicsList);
      }
      setRowData(response?.data?.content?.map(mapToRowData));
    }
  };

  React.useEffect(() => {
    getCountryUserRolesArray({ paged: false }, onGetRowData);
  }, [clinicsList]);

  return (
    <div className="h-full">
      {rowData && (
        <ManagementCrud
          getItemsArray={getCountryUserRolesArray}
          deleteItemById={onDeleteItem}
          customMapDataAfterDelete={onGetRowData}
          columns={columns}
          deleteConfirmText={'curMg.deleteCUR'}
          createButtonText={'curMg.createCUR'}
          itemIdKey={'id'}
          rowData={rowData}
        />
      )}
    </div>
  );
}

/**
 * Component definition of proptypes.
 */
CountryUserRolesManagementView.propTypes = {
  countryUserRoleActions: PropTypes.shape({
    getCountryUserRolesArray: PropTypes.func.isRequired
  }).isRequired,
  userActions: PropTypes.shape({
    deleteUserById: PropTypes.func.isRequired
  }).isRequired,
  user: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]).isRequired,
  dashboardStaffActions: PropTypes.shape({
    getAdmonCodesByEntity: PropTypes.func.isRequired
  })
};

export default CountryUserRolesManagementView;
